import { initializeApp } from 'firebase/app';
import { fetchAndActivate, getAll, getRemoteConfig, isSupported, Value } from 'firebase/remote-config';
import { FeatureFlag } from './featureFlags';
import { RemoteConfigKey, setNoddiRemoteConfig } from './remoteConfigSetup';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_CONFIG_API_KEY,
  authDomain: 'noddi-web.firebaseapp.com',
  projectId: 'noddi-web',
  storageBucket: 'noddi-web.firebasestorage.app',
  messagingSenderId: '255988546586',
  appId: '1:255988546586:web:98497d30297716210fa5a3',
  // cspell:disable-next-line -- Firebase measurement ID
  measurementId: 'G-3JKLRXLKCN'
};

const environment = import.meta.env.MODE;

const app = initializeApp(firebaseConfig);

// Set once a live fetch has activated values; a later failed refetch must not
// replace them with the static defaults (which turn maintenance screens off).
const remoteConfigState = { hasActivated: false };

export async function initRemoteConfig(): Promise<boolean> {
  try {
    const isRemoteConfigSupported = await isSupported();
    if (!isRemoteConfigSupported || environment === 'development') {
      setStaticRemoteConfig(true);
      return false;
    }

    const firebaseRemoteConfig = getRemoteConfig(app);
    firebaseRemoteConfig.settings.minimumFetchIntervalMillis = 30000;

    const success = await fetchAndActivate(firebaseRemoteConfig);

    setNoddiRemoteConfig(getAll(firebaseRemoteConfig));
    remoteConfigState.hasActivated = true;
    return success;
  } catch {
    // Remote config is optional; Safari can reject its IndexedDB storage after a user clears site data.
    if (!remoteConfigState.hasActivated) {
      setStaticRemoteConfig(false);
    }
    return false;
  }
}

function setStaticRemoteConfig(enableNewBookingFlow: boolean) {
  setNoddiRemoteConfig({
    maintenanceScreen: {
      asString: () => `{ "shouldShow": false }`,
      asBoolean: () => false,
      asNumber: () => 0,
      getSource: () => 'static'
    },
    maintenanceScreenBooking: {
      asString: () => `{ "shouldShow": false }`,
      asBoolean: () => false,
      asNumber: () => 0,
      getSource: () => 'static'
    },
    featureFlags: {
      asString: () => `{ "flags": { ${createFeatureFlagConfig('NEW_BOOKING_FLOW', enableNewBookingFlow)} } }`,
      asBoolean: () => false,
      asNumber: () => 0,
      getSource: () => 'static'
    }
  } satisfies Partial<Record<RemoteConfigKey, Value>>);
}

function createFeatureFlagConfig(flag: FeatureFlag, enabled: boolean) {
  return `"${flag}": { "enabled": ${enabled} }`;
}
